/**
 * Shared chrome for the pre-app screens: login, registration, approval wait,
 * rejection and store setup.
 *
 * These five are one continuous flow — a merchant can traverse all of them in a
 * single sitting on their first day — so they must look like one surface rather
 * than five. The ground stays light and the card alone is charcoal glass; `dark
 * chrome` is scoped to the card so the semantic tokens re-point for that subtree
 * only, exactly as LoginScreen and AppShell do it (see app/theme.css).
 *
 * The field and button classes live here so the forms cannot drift apart.
 */
import { useErrorPopup } from '@/hooks/useErrorPopup';
import type { ReactNode } from 'react';
import { BrandMark } from '@/components/BrandMark';

/** Translucent on charcoal, never an opaque grey slab. */
export const FIELD =
  'w-full rounded-lg border border-white/15 bg-white/[0.06] p-3 text-body text-fg-primary ' +
  'placeholder:text-fg-tertiary backdrop-blur-sm transition-colors duration-120 ease-apple ' +
  'hover:border-white/25 focus:border-accent/60 focus:bg-white/10';

/**
 * The app-wide primary, unchanged on the charcoal card.
 *
 * This used to be a lighter-charcoal slab, because the old primary was dark
 * glass and would have disappeared into a dark card. Now that primary is Bigx
 * Blue it reads clearly on charcoal *and* on the light sheet, so the sign-in
 * and onboarding CTAs are the same button as everywhere else — which is what
 * you want on the two screens where the action is the whole point.
 *
 * `.btn-brand` keeps its own solid ground in every scope (see `--accent-solid`
 * in theme.css), so it does not lighten just because the card around it is
 * dark.
 */
export const PRIMARY =
  'btn-brand w-full rounded-lg border border-transparent p-3 text-body font-medium ' +
  'transition-colors duration-120 ease-apple disabled:opacity-40 disabled:shadow-none';

/**
 * The second real action on a screen that genuinely has two.
 *
 * Outlined rather than filled: it must read as a button at a glance — the
 * whole point is that someone who has never signed in before can see the
 * registration path without hunting for it — while still losing the contest
 * for attention to the blue primary beside it. QUIET is not enough for that
 * job; it looks like a link and was skipped past on the front door.
 */
export const SECONDARY =
  'w-full rounded-lg border border-white/25 bg-white/[0.04] p-3 text-body font-medium ' +
  'text-fg-primary transition-colors duration-120 ease-apple ' +
  'hover:border-white/40 hover:bg-white/[0.10] disabled:opacity-40';

export const QUIET =
  'rounded-lg p-2 text-body text-fg-secondary transition-colors duration-120 ease-apple ' +
  'hover:bg-white/[0.06] hover:text-fg-primary';

export const LABEL = 'text-label uppercase text-fg-tertiary';

export function OnboardingCard({
  title,
  subtitle,
  error,
  children,
  footer,
  /** `sm` for the login-sized forms, `lg` for the multi-field ones. */
  width = 'sm',
}: {
  title: string;
  subtitle?: ReactNode;
  error?: string | null;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'lg';
}) {
  useErrorPopup(error);
  return (
    <main className="login-backdrop grid min-h-screen place-items-center p-6">
      <div
        className={`dark chrome glass-charcoal w-full rounded-2xl border border-white/10 p-8 ${
          width === 'lg' ? 'max-w-lg' : 'max-w-sm'
        }`}
      >
        {/* Brand ramp, matching LoginScreen — onboarding is the same surface,
            still pre-till. */}
        <header className="mb-7">
          <div className="mb-5 flex items-center gap-3">
            <BrandMark size={36} />
            <h1 className="text-brand-h2 tracking-tight">BigxBook</h1>
          </div>
          <h2 className="text-brand-h4 text-fg-primary">{title}</h2>
          {subtitle && <p className="mt-1.5 text-brand-small text-fg-secondary">{subtitle}</p>}
        </header>

        {children}

        {footer && <div className="mt-6 border-t border-white/10 pt-4">{footer}</div>}
      </div>
    </main>
  );
}

/** Labelled text input. Keeps label/input/hint spacing identical across forms. */
export function Field({
  id,
  label,
  hint,
  required,
  ...props
}: {
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={LABEL}>
        {label}
        {required && <span className="ml-1 text-danger-ink">*</span>}
      </label>
      <input id={id} className={FIELD} {...props} />
      {hint && <p className="text-label text-fg-tertiary">{hint}</p>}
    </div>
  );
}
